import { useState, useEffect } from 'react';
import { CheckCircleIcon, XMarkIcon, ExclamationTriangleIcon, ClockIcon, UserMinusIcon } from '@heroicons/react/24/outline';

/**
 * Format elapsed time as "Xh Ym" or "Xm" or "Xs"
 */
function formatElapsed(startTime) {
  if (!startTime) return null;
  const seconds = Math.floor((Date.now() - startTime) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

/**
 * Sticky footer bar showing session progress and conclude button.
 * Appears when staff start actioning consequences in the queue.
 */
export default function ConcludeSessionBar({
  sessionSummary,
  sessionStartTime,
  sessionStatus,
  recovery,
  consequenceTypeLabel,
  remainingCount = 0,
  requireComplete = false,
  onConclude,
  onDismiss,
}) {
  // Tick the timer every second
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!sessionStartTime) return;
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [sessionStartTime]);

  if (sessionSummary.total === 0 && !sessionStatus) return null;

  const elapsed = formatElapsed(sessionStartTime);
  const registerSize = requireComplete ?
    sessionSummary.total + remainingCount : sessionSummary.total;
  const sessionLabel = consequenceTypeLabel || 'Action';

  const badges = [
    { key: 'attended', count: sessionSummary.attended, label: 'Attended', icon: CheckCircleIcon, className: 'badge-success' },
    { key: 'missed', count: sessionSummary.missed, label: 'Missed', icon: ExclamationTriangleIcon, className: 'badge-error' },
    { key: 'late', count: sessionSummary.late, label: 'Late', icon: ClockIcon, className: 'badge-warning' },
    { key: 'absent', count: sessionSummary.absent, label: 'Absent', icon: UserMinusIcon, className: 'badge-info' },
  ].filter(b => b.count > 0);

  return (
    <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 z-50 bg-base-100 border-t border-base-300 shadow-lg">
      <div className="max-w-4xl mx-auto px-4 py-3">
        {/* Row 1: Session label + timer + dismiss */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-base-content/70">
              {sessionLabel} session
            </span>
            <span className="text-xs text-base-content/60">
              {requireComplete ?
                `${sessionSummary.total} of ${registerSize} recorded` :
                `${sessionSummary.total} recorded`}
            </span>
            {elapsed && (
              <span className="text-xs font-mono text-base-content/50">
                <ClockIcon className="w-3 h-3 inline mr-0.5 -mt-0.5" />
                {elapsed}
              </span>
            )}
          </div>
          <button
            onClick={onDismiss}
            className="btn btn-ghost btn-xs"
            title="Minimise session register"
            aria-label="Minimise session register"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {sessionStatus === 'recovery_required' && recovery?.reason !== 'incomplete_roster' && (
          <div className="alert alert-warning py-2 px-3 mb-2 text-sm">
            <ExclamationTriangleIcon className="w-4 h-4" />
            <span>
              Session needs recovery · {(recovery?.missingRecordIds || []).length} learner
              {(recovery?.missingRecordIds || []).length === 1 ? '' : 's'} still unmarked
            </span>
          </div>
        )}
        {sessionStatus === 'recovering' && (
          <div className="alert alert-info py-2 px-3 mb-2 text-sm">
            <ClockIcon className="w-4 h-4" />
            <span>Recovering session…</span>
          </div>
        )}
        {requireComplete && remainingCount > 0 && sessionStatus !== 'recovery_required' && (
          <div className="alert alert-warning py-2 px-3 mb-2 text-sm">
            <ExclamationTriangleIcon className="w-4 h-4" />
            <span>
              Session remains open · {remainingCount} learner
              {remainingCount === 1 ? '' : 's'} still unmarked
            </span>
          </div>
        )}
        {/* Row 2: Badges + conclude button */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {badges.map(({ key, count, label, icon: Icon, className }) => (
              <span key={key} className={`badge ${className} gap-1`}>
                <Icon className="w-3.5 h-3.5" />
                {count} {label}
              </span>
            ))}
            <span className="text-xs text-base-content/50">
              ({sessionSummary.total} total)
            </span>
          </div>
          <button
            onClick={onConclude}
            disabled={
              sessionSummary.total === 0 ||
              sessionStatus === 'recovering' ||
              (requireComplete && remainingCount > 0)
            }
            className="btn btn-primary btn-sm shrink-0 gap-2"
          >
            <CheckCircleIcon className="w-4 h-4" />
            <span className="hidden sm:inline">Conclude Session</span>
            <span className="sm:hidden">Conclude</span>
          </button>
        </div>
      </div>
    </div>
  );
}
